import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { modal } from '@client/components/_ui/feedback/modal';
import { getConfig } from '@client/config/runtime';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterInputs } from '@client/trpc/client';
import { ADMIN_ROLES, AUTH0_CONNECTIONS, type Auth0Connection } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import {
    Checkbox,
    Divider,
    Drawer,
    Form,
    Input,
    Popconfirm,
    Select,
    Skeleton,
    Space,
    Switch,
    Tag,
    Typography,
} from 'antd';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/admin-console/user-access/users/edit/$userId')({
    params: {
        parse({ userId }) {
            return { userId: BigInt(userId) };
        },
    },
    staticData: {
        presentation: 'overlay',
    },
    component: EditUserRoute,
});

type User = RouterInputs['admin']['user']['updateUser']['set'] & {
    allowedAuthMethods?: string[];
};

const FormItem = Form.Item<User>;

function getEmailChangeConfirmationMessage(authMode: 'password' | 'sso' | 'pending-invite' | 'pre-activation'): string {
    switch (authMode) {
        case 'password':
            return "This will change the user's login email address. The user may need to verify their new email address.\n\nThis action will be recorded in the audit trail.";
        case 'sso':
            return "This will update the email in Curium's records and reset this account's SSO link. Whoever signs in next with the new email via Azure AD will inherit this account's roles, organisations and history. Make sure the new email belongs to the correct person before continuing.\n\nThe user's Azure AD login email itself is not affected — update it in Azure AD separately if needed.\n\nThis action will be recorded in the audit trail.";
        case 'pending-invite':
            return 'This will update the email for this pending invitation and invalidate the current invite link across all organisations. You will need to resend the invitation.\n\nThis action will be recorded in the audit trail.';
        case 'pre-activation':
            return "This will update the email for this SSO user who has not yet completed their first login. Ensure the new email matches the user's Azure AD identity so they can complete their first sign-on.\n\nThis action will be recorded in the audit trail.";
        default:
            return "This will change the user's email address.\n\nThis action will be recorded in the audit trail.";
    }
}

function EditUserRoute() {
    const { userId } = Route.useParams();
    const { trpc, trpcUtils } = useTrpc();
    const { formatDateTime } = useFormatter();
    const navigate = useNavigate();
    const currentUser = useUser();
    const [isProcessing, setIsProcessing] = useState(false);
    const [open, setOpen] = useState(true);
    const [newEmail, setNewEmail] = useState('');

    const isSuperAdmin = currentUser?.roles.includes(ADMIN_ROLES.SUPER_ADMIN) ?? false;

    const [form] = Form.useForm<User>();

    const { data: user, isLoading } = trpc.admin.user.getUser.useQuery({ userId }, { refetchOnWindowFocus: false });
    const { mutateAsync: updateUser } = trpc.admin.user.updateUser.useMutation();
    const { mutateAsync: disableInAllOrgs } = trpc.admin.user.disableInAllOrgs.useMutation();
    const { mutateAsync: changeUserEmail, isPending: isChangingEmail } = trpc.admin.user.changeUserEmail.useMutation();

    useEffect(() => {
        if (user) {
            form.resetFields();
            form.setFieldsValue({
                firstName: user.firstName ?? '',
                lastName: user.lastName ?? '',
                isEmailVerified: user.isEmailVerified ?? false,
                auth0Id: user.auth0Id,
                syncWithAuth0InDev: user.syncWithAuth0InDev ?? false,
                provider: (user.provider ?? undefined) as Auth0Connection | undefined,
                allowedAuthMethods: user.allowedAuthMethods ?? [],
            });
            setNewEmail(user.email ?? '');
        }
    }, [user]);

    const onSave = async (formData: User) => {
        try {
            setIsProcessing(true);
            message.loading('Saving...');

            // Only send auth0Id when it actually changed. A cleared field means "leave unchanged"
            // (the server rejects blank auth0Ids), and an unchanged pre-filled value is left
            // untouched so a legacy pipe-less id doesn't trip the server's stricter validation on
            // an unrelated edit (CUR-399).
            const trimmedAuth0Id = formData.auth0Id?.trim();
            const auth0IdChanged = !!trimmedAuth0Id && trimmedAuth0Id !== user?.auth0Id?.trim();

            await updateUser({
                userId: userId,
                set: {
                    isEmailVerified: !!formData.isEmailVerified,
                    auth0Id: auth0IdChanged ? trimmedAuth0Id : undefined,
                    syncWithAuth0InDev: !!formData.syncWithAuth0InDev,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    provider: formData.provider,
                    allowedAuthMethods: formData.allowedAuthMethods,
                },
            });
            message.success('Saved.');
            setOpen(false);
        } catch (error) {
            errorMessage.show(error);
        } finally {
            setIsProcessing(false);
        }
    };

    const onDisable = async () => {
        try {
            setIsProcessing(true);
            message.loading('Disabling...');
            await disableInAllOrgs({
                userId: userId,
            });
            message.success('Disabled.');
        } catch (error) {
            errorMessage.show(error);
        } finally {
            setIsProcessing(false);
        }
    };

    const onChangeEmail = () => {
        if (!user || !newEmail || newEmail.toLowerCase() === user.email?.toLowerCase()) {
            return;
        }

        const authMode = user.authMode ?? 'password';
        const confirmationMessage = getEmailChangeConfirmationMessage(authMode);

        modal.confirm({
            title: 'Change email address',
            content: (
                <div className="whitespace-pre-line">
                    <Typography.Text>{confirmationMessage}</Typography.Text>
                    <div className="mt-3 rounded-md bg-muted p-2">
                        <Typography.Text type="secondary" className="text-xs">
                            {user.email} → {newEmail}
                        </Typography.Text>
                    </div>
                </div>
            ),
            okText: 'Change email',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    const result = await changeUserEmail({
                        userId,
                        newEmail: newEmail.toLowerCase().trim(),
                    });

                    const successNote =
                        "The user's email has been updated across all organisations." +
                        (result.authMode === 'pending-invite' ? ' Pending invitations will need to be resent.' : '');

                    message.success(successNote);

                    // Invalidate both getUser and listUsers queries
                    void trpcUtils.admin.user.getUser.invalidate({ userId });
                    void trpcUtils.admin.user.listUsers.invalidate();
                } catch (error) {
                    errorMessage.show(error);
                }
            },
        });
    };

    return (
        <PageController route={Route}>
            <Drawer
                open={open}
                onClose={() => setOpen(false)}
                afterOpenChange={(isOpen) => {
                    if (!isOpen) {
                        void trpcUtils.admin.user.listUsers.invalidate();
                        navigate({ to: '/admin-console/user-access/users' });
                    }
                }}
                size={600}
                mask={{ closable: false }}
                title={'Edit User'}
                footer={
                    <Space className="flex justify-end">
                        <Button onClick={() => setOpen(false)}>Cancel</Button>
                        <Popconfirm
                            title="Are you sure you want to disable this user across all organisations?"
                            onConfirm={onDisable}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button type="primary">Disable across all orgs</Button>
                        </Popconfirm>
                        <Button type="primary" htmlType="submit" block form="editForm" loading={isProcessing}>
                            Save
                        </Button>
                    </Space>
                }
            >
                {isLoading || !user ? (
                    <Skeleton active />
                ) : (
                    <Form id="editForm" layout="vertical" form={form} onFinish={onSave}>
                        {/* Account Information Section */}
                        <Typography.Title level={5} className="mb-4">
                            Account information
                        </Typography.Title>
                        <div className="mb-6">
                            <FormItem label="First name" name="firstName">
                                <Input placeholder="Enter first name" />
                            </FormItem>
                            <FormItem label="Last name" name="lastName">
                                <Input placeholder="Enter last name" />
                            </FormItem>
                            <FormItem label="Email">
                                {isSuperAdmin ? (
                                    <div className="flex gap-2">
                                        <Input
                                            value={newEmail}
                                            onChange={(e) => setNewEmail(e.target.value)}
                                            placeholder="Email address"
                                        />
                                        <Button
                                            onClick={onChangeEmail}
                                            loading={isChangingEmail}
                                            disabled={!newEmail || newEmail.toLowerCase() === user.email?.toLowerCase()}
                                        >
                                            Change email
                                        </Button>
                                    </div>
                                ) : (
                                    <>
                                        <Input value={user.email || ''} readOnly className="bg-muted" />
                                        <Typography.Text type="secondary" className="text-xs">
                                            Email address cannot be changed here
                                        </Typography.Text>
                                    </>
                                )}
                            </FormItem>
                            <FormItem
                                label="Email verification status"
                                name="isEmailVerified"
                                valuePropName="checked"
                                help="Controls whether the user can access the system"
                            >
                                <Switch
                                    checkedChildren="Verified"
                                    unCheckedChildren="Unverified"
                                    className="ant-switch-success-warning"
                                />
                            </FormItem>
                        </div>

                        <Divider />

                        {/* Authentication Section */}
                        <Typography.Title level={5} className="mb-4">
                            Authentication
                        </Typography.Title>
                        <div className="mb-6">
                            <FormItem label="Provider" name="provider">
                                <Select
                                    placeholder="Select authentication provider"
                                    options={Object.values(AUTH0_CONNECTIONS).map((connection) => ({
                                        label: connection.label,
                                        value: connection.name,
                                    }))}
                                />
                            </FormItem>
                            <FormItem
                                label="Allowed authentication methods for sign up"
                                name="allowedAuthMethods"
                                tooltip="Select which authentication methods this user can use to sign in. If empty, all methods are allowed."
                                rules={[
                                    {
                                        validator: async (_, value) => {
                                            if (!value || value.length === 0) {
                                                // Allow empty array - means all methods are allowed
                                                return Promise.resolve();
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="Select allowed authentication methods (leave empty for all)"
                                    options={Object.values(AUTH0_CONNECTIONS).map((connection) => ({
                                        label: connection.label,
                                        value: connection.name,
                                    }))}
                                    allowClear
                                />
                            </FormItem>
                            <FormItem label="Auth0 ID" name="auth0Id">
                                <Input placeholder="Auth0 user identifier" />
                                <Typography.Text type="secondary" className="text-xs">
                                    Unique identifier from Auth0
                                </Typography.Text>
                            </FormItem>
                        </div>

                        <Divider />

                        {/* System Settings Section */}
                        <Typography.Title level={5} className="mb-4">
                            System settings
                        </Typography.Title>
                        <div className="mb-6">
                            {/* Admin access info - managed via Admin users page */}
                            <div className="mb-4 rounded-md border border-separator bg-muted p-3">
                                <Typography.Text type="secondary" className="text-sm">
                                    Admin console access is managed via the{' '}
                                    <Typography.Link href="/admin-console/user-access/admin-users">
                                        Admin users
                                    </Typography.Link>{' '}
                                    page.
                                </Typography.Text>
                                {user.isAdmin && (
                                    <div className="mt-2">
                                        <Tag color="gold">Admin Access Enabled</Tag>
                                    </div>
                                )}
                            </div>
                        </div>

                        {getConfig('ENV', import.meta.env.VITE_ENV) === 'development' && (
                            <>
                                <Divider />
                                <Typography.Title level={5} className="mb-4">
                                    Development settings
                                </Typography.Title>
                                <div className="mb-6">
                                    <FormItem
                                        name="syncWithAuth0InDev"
                                        valuePropName="checked"
                                        help="Enable Auth0 synchronization in development environment"
                                    >
                                        <Checkbox>
                                            <span className="font-medium">Sync with Auth0 in Development</span>
                                        </Checkbox>
                                    </FormItem>
                                </div>
                                <div className="rounded-md bg-muted p-4">
                                    <div className="flex items-center justify-between">
                                        <Typography.Text type="secondary">Last synchronised:</Typography.Text>
                                        <Typography.Text className="font-medium">
                                            {user.lastSynced ? formatDateTime(user.lastSynced) : 'Never'}
                                        </Typography.Text>
                                    </div>
                                </div>
                            </>
                        )}
                    </Form>
                )}
            </Drawer>
        </PageController>
    );
}
